// =============================================================================
// features/conversations/components/ConversationPanel.tsx — Painel da conversa.
//
// Composição:
//   - useMessages(conversationId): histórico infinito (cursor backwards)
//   - useConversationSocket: realtime (message:new / conversation:updated)
//   - Scroll container com Intersection Observer (load-more ao topo)
//   - Auto-scroll para o bottom em novas mensagens
//   - MessageBubble: polimórfico por tipo
//   - MessageComposer: input + janela 24h
//
// Loading state: skeleton de bolhas (nunca spinner sozinho).
// Empty state: CTA de início de conversa.
// Error state: mensagem clara + retry.
//
// LGPD (doc 17):
//   - Não loga conteúdo de mensagens
//   - Skeleton não exibe dados reais
// =============================================================================

import * as React from 'react';

import { formatDateTimeOrgTz } from '../../../hooks/sales/orgTimezone';
import { useContactPreferences } from '../../../hooks/sales/useContactPreferences';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';
import { useConversationSocket } from '../hooks/useConversationSocket';
import { getInboxMarkerByIndex, getInboxMarkerStyle } from '../inboxColor';
import { useAckBriefing, useConversation, useMessages } from '../queries';
import type { ChannelProvider, ConversationBriefingRef, Message } from '../types';

import { HandoffBriefingModal } from './HandoffBriefingModal';
import { MessageBubble } from './MessageBubble';
import { isDifferentDay, formatDaySeparator } from './MessageBubble/utils';
import { MessageComposer } from './MessageComposer';
import { ScheduleReturnButton } from './ScheduleReturnButton';

// ─── Gatilho do briefing de handoff (F37-S06, doc 28 §2.2/§3 D3/D5) ────────
//
// Flag `livechat.ai_briefing.enabled` — mesma chave de `QualificationRuler`
// (cópia local intencional, mesma decisão de duplicação já usada no módulo).
// Sem a flag, nada renderiza nem chama API (DoD do slot): a régua de
// `useConversation` já traria `briefing` de qualquer forma (o backend não
// gateia esse campo por flag), então o gate aqui é o único portão do front.
const BRIEFING_FLAG_KEY = 'livechat.ai_briefing.enabled';

/**
 * shouldOpenBriefingModal — decide se o modal deve abrir para este briefing.
 *
 * Guard contra reabertura por StrictMode (efeito roda 2x em dev) e por
 * refetch/socket (`briefing` é um objeto NOVO a cada resposta, mesmo com os
 * mesmos valores) — `alreadyOpenedIds` é um Set persistido em `useRef` no
 * componente, nunca resetado enquanto a conversa estiver montada. Uma vez
 * aberto (id adicionado ao Set), NUNCA reabre sozinho — mesmo que o socket
 * invalide o detalhe de novo (ex.: `briefing_ready` também invalida
 * `conversationKeys.detail`).
 *
 * Extraída como função pura para ser testável isoladamente (ver
 * __tests__/HandoffBriefingModal.test.ts) — a MESMA lógica que
 * `ConversationPanel` usa dentro do `useEffect`.
 */
export function shouldOpenBriefingModal(
  briefing: ConversationBriefingRef | null | undefined,
  alreadyOpenedIds: ReadonlySet<string>,
): string | null {
  if (!briefing) return null;
  if (briefing.firstViewedAt !== null) return null;
  if (alreadyOpenedIds.has(briefing.id)) return null;
  return briefing.id;
}

/**
 * shouldFireBriefingAck — garante NO MÁXIMO 1 chamada de ack por
 * `briefingId`, mesmo que o fechamento seja disparado por múltiplos caminhos
 * (Esc + clique no overlay quase simultâneos, ex.) — `alreadyAckedIds` é o
 * mesmo tipo de Set persistido via `useRef` de `shouldOpenBriefingModal`.
 */
export function shouldFireBriefingAck(
  briefingId: string | null,
  alreadyAckedIds: ReadonlySet<string>,
): boolean {
  return briefingId !== null && !alreadyAckedIds.has(briefingId);
}

/**
 * commitBriefingAck — dispara `ackFn` e só marca `briefingId` como ackado
 * (adiciona ao Set) **depois** da confirmação do servidor.
 *
 * Corrige o bug de "ack otimista": marcar o Set antes do resultado da
 * mutação faz o guard de `shouldFireBriefingAck` travar em `false` pra
 * sempre, mesmo que o POST `/briefing/ack` falhe (timeout, 5xx) e o backend
 * nunca grave `firstViewedAt` — o padrão "idempotência que ignora o status
 * anterior mascara falha" (ver MEMORY: outbox 2026-08-03). Em falha, o Set
 * NÃO é tocado — uma tentativa futura de ack para o mesmo `briefingId` (ex.:
 * o usuário reabre a conversa e fecha o modal de novo) ainda pode disparar
 * o POST. Não força retry automático nem reabre o modal sozinho.
 */
export async function commitBriefingAck(
  briefingId: string,
  ackedBriefingIds: Set<string>,
  ackFn: () => Promise<unknown>,
): Promise<void> {
  try {
    await ackFn();
    ackedBriefingIds.add(briefingId);
  } catch {
    // Falha no ack: Set não é marcado — próxima tentativa pode reenviar.
  }
}

// ─── Banner de restrição de contato acima do composer (F38-S04, doc
// planejamento-livechat-contexto-comercial.md §2.2.1 "Banner de restrição no
// composer") ──────────────────────────────────────────────────────────────
//
// Canal WhatsApp + restrição EFETIVA (`do_not_contact` ou opt-out do canal
// `whatsapp` não expirado) → banner âmbar persistente acima do composer,
// com motivo e validade. NÃO bloqueia o envio (decisão de produto do plano
// — pode haver resposta legítima a um contato iniciado pelo cliente, Art.
// 7º V da LGPD): só informa, o composer segue habilitado.
//
// Mesmo gate de leitura de `useContactPreferences` em todo o módulo
// comercial (`sales.appointments.enabled` + `sales_appointments:read`, ver
// apps/api/src/modules/sales/preferences/routes.ts READ_PERMS) — cópia
// local da constante (mesma decisão de `CommercialSection.tsx`).
const RESTRICTION_FLAG_KEY = 'sales.appointments.enabled';
const RESTRICTION_READ_PERMISSION = 'sales_appointments:read';

function ContactRestrictionComposerBanner({
  leadId,
  provider,
}: {
  leadId: string | null;
  provider: ChannelProvider | undefined;
}): React.JSX.Element | null {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled } = useFeatureFlag(RESTRICTION_FLAG_KEY);
  const canRead = hasPermission(RESTRICTION_READ_PERMISSION) && flagEnabled;
  const isWhatsapp = provider === 'meta_whatsapp';

  const { blocked } = useContactPreferences(leadId ?? '', {
    enabled: canRead && isWhatsapp && leadId !== null,
  });

  if (!canRead || !isWhatsapp || leadId === null) return null;

  const whatsappBlock = blocked.find((b) => b.channel === 'whatsapp');
  if (!whatsappBlock) return null;

  const reason = whatsappBlock.reason?.trim() ? whatsappBlock.reason : 'Motivo não informado.';
  const expiresLabel = whatsappBlock.expiresAt
    ? formatDateTimeOrgTz(whatsappBlock.expiresAt)
    : null;

  return (
    <div
      role="status"
      className="flex items-start gap-2.5 px-4 py-2.5 flex-shrink-0"
      style={{
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--warning-bg)',
      }}
    >
      <span
        aria-hidden="true"
        className="shrink-0 w-5 h-5 rounded-pill flex items-center justify-center mt-0.5"
        style={{ background: 'var(--warning)', color: 'var(--text-on-brand)' }}
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          className="w-3 h-3"
        >
          <path d="M8 5v3.5M8 10.5v.01" strokeLinecap="round" />
          <path d="M6.9 2.6a1.28 1.28 0 0 1 2.2 0l5.1 8.8a1.28 1.28 0 0 1-1.1 1.9H2.9a1.28 1.28 0 0 1-1.1-1.9Z" />
        </svg>
      </span>
      <p className="font-sans text-xs text-ink-2 leading-relaxed">
        <span className="font-semibold" style={{ color: 'var(--warning)' }}>
          Este contato pediu para não ser contatado por WhatsApp.
        </span>{' '}
        {reason}
        {expiresLabel && <span className="text-ink-3"> · válido até {expiresLabel}</span>}
      </p>
    </div>
  );
}

// ─── ConversationHeader ───────────────────────────────────────────────────────

function ProviderBadge({ provider }: { provider: ChannelProvider }): React.JSX.Element {
  const color =
    provider === 'meta_whatsapp'
      ? '#25d366'
      : provider === 'meta_instagram'
        ? '#e1306c'
        : 'var(--brand-azul)';

  const label =
    provider === 'meta_whatsapp'
      ? 'WhatsApp'
      : provider === 'meta_instagram'
        ? 'Instagram'
        : 'Chat';

  return (
    <span
      className="inline-flex items-center gap-1 rounded-pill font-sans font-medium"
      style={{
        padding: '2px 8px',
        fontSize: 11,
        background: `color-mix(in srgb, ${color} 15%, transparent)`,
        color,
        border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
      }}
    >
      {label}
    </span>
  );
}

interface ConversationHeaderProps {
  conversationId: string;
  /**
   * Se fornecido, o bloco nome+avatar vira um botão que abre o painel de
   * contato. Usado principalmente no mobile (toque no nome do cliente), onde
   * o painel lateral não cabe — no desktop ele já fica visível/togglável.
   */
  onOpenContact?: (() => void) | undefined;
}

function ConversationHeader({
  conversationId,
  onOpenContact,
}: ConversationHeaderProps): React.JSX.Element {
  const { data } = useConversation(conversationId);

  const contactName = data?.data.contactName ?? data?.data.contactRemoteId ?? '…';
  const provider = data?.composerState.provider;
  const initial = contactName.charAt(0).toUpperCase();
  const leadId = data?.data.leadId ?? null;
  const ticketNumber = data?.data.ticketNumber;
  // F47-S09: nome/cor da inbox vêm PRONTOS no DTO da conversa — nunca de
  // `GET /api/channels` (gateado por `channel.connect`, invisível para a
  // maior parte da operação). O front só mapeia índice → marcador (cor +
  // forma) do design system (`getInboxMarkerByIndex`); não recalcula ranking.
  const channelName = data?.data.channelName;
  const channelColorIndex = data?.data.channelColorIndex;
  const inboxMarkerStyle =
    channelColorIndex !== undefined
      ? getInboxMarkerStyle(getInboxMarkerByIndex(channelColorIndex))
      : null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
      style={{
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-elev-1)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      {/* Nome + avatar — clicável para abrir os dados do contato (doc: mobile) */}
      <button
        type="button"
        onClick={onOpenContact}
        disabled={onOpenContact === undefined}
        className={cn(
          'flex items-center gap-3 min-w-0 flex-1 text-left rounded-sm -mx-1 px-1 py-0.5',
          'transition-colors duration-fast ease',
          'enabled:cursor-pointer enabled:hover:bg-surface-hover',
          'disabled:cursor-default',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
        )}
        aria-label={onOpenContact !== undefined ? `Ver informações de ${contactName}` : undefined}
        title={onOpenContact !== undefined ? 'Ver informações do contato' : undefined}
      >
        {/* Avatar */}
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-full font-sans font-bold select-none"
          style={{
            width: 36,
            height: 36,
            background: 'var(--grad-azul)',
            color: 'var(--brand-branco)',
            fontSize: 'var(--text-sm)',
            boxShadow: 'var(--elev-2)',
          }}
          aria-hidden="true"
        >
          {initial}
        </span>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col gap-0.5 items-start">
          <span
            className="font-sans font-semibold truncate max-w-full"
            style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', letterSpacing: '-0.01em' }}
          >
            {contactName}
          </span>
          {/* Ticket + inbox (F47-S07/S09) — visível em qualquer status */}
          {ticketNumber !== undefined && channelName !== undefined && (
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="font-mono font-medium flex-shrink-0"
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-3)',
                  letterSpacing: '-0.01em',
                }}
              >
                #{ticketNumber}
              </span>
              {inboxMarkerStyle !== null && (
                <>
                  <span aria-hidden="true" className="flex-shrink-0" style={inboxMarkerStyle} />
                  <span
                    className="font-sans truncate"
                    title={channelName}
                    style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}
                  >
                    {channelName}
                  </span>
                </>
              )}
            </div>
          )}
          {provider !== undefined && <ProviderBadge provider={provider} />}
        </div>
      </button>

      {/* Agendar retorno (F33-S08, doc 26 §6.4) — só aparece com permissão
          `sales_appointments:write` + flag `sales.appointments.enabled`
          (gate interno do próprio componente). */}
      <ScheduleReturnButton leadId={leadId} leadName={contactName} />

      {/* Botão "i" — abre os dados do contato no MOBILE (o desktop usa o botão
          flutuante do ConversationsLayout). `md:hidden` esconde ≥768px. Mesmo
          ícone do toggle do desktop. */}
      {onOpenContact !== undefined && (
        <button
          type="button"
          onClick={onOpenContact}
          aria-label="Ver informações do contato"
          title="Ver informações do contato"
          className="md:hidden shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-sm transition-colors duration-fast ease hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30"
          style={{ border: '1px solid var(--border-subtle)', color: 'var(--text-3)' }}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <circle cx="8" cy="8" r="6.5" />
            <path d="M8 7v4" />
            <circle cx="8" cy="5" r=".5" fill="currentColor" stroke="none" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Tipos internos ───────────────────────────────────────────────────────────

type ListItem = { kind: 'message'; message: Message } | { kind: 'day-separator'; date: string };

// ─── Skeleton de carregamento ─────────────────────────────────────────────────

function MessageSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 animate-pulse">
      {/* Inbound */}
      <div className="flex gap-2 items-end">
        <div className="w-7 h-7 rounded-pill bg-surface-muted shrink-0" />
        <div className="flex flex-col gap-1 max-w-[60%]">
          <div className="h-10 rounded-md bg-surface-muted w-48" />
          <div className="h-3 rounded bg-surface-muted w-16" />
        </div>
      </div>
      {/* Outbound */}
      <div className="flex justify-end gap-2 items-end">
        <div className="flex flex-col gap-1 items-end max-w-[60%]">
          <div className="h-8 rounded-md bg-surface-muted w-40" />
          <div className="h-3 rounded bg-surface-muted w-12" />
        </div>
      </div>
      {/* Inbound longa */}
      <div className="flex gap-2 items-end">
        <div className="w-7 h-7 rounded-pill bg-surface-muted shrink-0" />
        <div className="flex flex-col gap-1 max-w-[70%]">
          <div className="h-16 rounded-md bg-surface-muted w-56" />
          <div className="h-3 rounded bg-surface-muted w-16" />
        </div>
      </div>
      {/* Outbound */}
      <div className="flex justify-end">
        <div className="flex flex-col gap-1 items-end max-w-[50%]">
          <div className="h-6 rounded-md bg-surface-muted w-32" />
          <div className="h-3 rounded bg-surface-muted w-10" />
        </div>
      </div>
      {/* Inbound */}
      <div className="flex gap-2 items-end">
        <div className="w-7 h-7 rounded-pill bg-surface-muted shrink-0" />
        <div className="flex flex-col gap-1">
          <div className="h-8 rounded-md bg-surface-muted w-36" />
          <div className="h-3 rounded bg-surface-muted w-14" />
        </div>
      </div>
    </div>
  );
}

// ─── DaySeparator ─────────────────────────────────────────────────────────────

function DaySeparator({ date }: { date: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center gap-3 py-2 px-4">
      <div className="h-px flex-1 bg-border-subtle" />
      <span className="font-sans text-xs text-ink-3 px-2 py-0.5 rounded-pill bg-surface-2 border border-border-subtle whitespace-nowrap">
        {date}
      </span>
      <div className="h-px flex-1 bg-border-subtle" />
    </div>
  );
}

// ─── LoadMoreTrigger ──────────────────────────────────────────────────────────

interface LoadMoreTriggerProps {
  onIntersect: () => void;
  hasMore: boolean;
  isFetching: boolean;
}

/**
 * Sentinela invisível no topo da lista.
 * Quando fica visível (usuário rolou até o topo), dispara `onIntersect`.
 */
function LoadMoreTrigger({
  onIntersect,
  hasMore,
  isFetching,
}: LoadMoreTriggerProps): React.JSX.Element | null {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!hasMore || isFetching) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          onIntersect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetching, onIntersect]);

  if (!hasMore) return null;

  return (
    <div ref={ref} className="flex justify-center py-3">
      {isFetching ? (
        <div className="flex items-center gap-2 text-ink-3 text-xs font-sans">
          <svg
            className="w-3.5 h-3.5 animate-spin"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <circle cx="8" cy="8" r="5" strokeOpacity="0.25" />
            <path d="M8 3a5 5 0 015 5" strokeLinecap="round" />
          </svg>
          Carregando mensagens anteriores...
        </div>
      ) : (
        <button
          type="button"
          onClick={onIntersect}
          className="font-sans text-xs text-azul hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30 rounded-xs px-2 py-1 transition-colors"
        >
          Carregar mensagens anteriores
        </button>
      )}
    </div>
  );
}

// ─── MessageList ──────────────────────────────────────────────────────────────

interface MessageListProps {
  items: ListItem[];
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
  /** Total de mensagens antes da última atualização — para auto-scroll */
  prevCount: number;
  /** Inicial do nome do contato para o avatar inbound */
  contactInitial: string;
}

function MessageList({
  items,
  onLoadMore,
  hasMore,
  isFetchingMore,
  prevCount,
  contactInitial,
}: MessageListProps): React.JSX.Element {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const isInitialMount = React.useRef(true);

  // Scroll inicial para o bottom — roda somente quando items são carregados pela 1ª vez.
  React.useEffect(() => {
    if (isInitialMount.current && items.length > 0) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
      isInitialMount.current = false;
    }
  }, [items.length]);

  // Auto-scroll para o bottom quando novas mensagens são adicionadas ao final.
  // deps: [items.length, prevCount] — evita execução em todo re-render.
  React.useEffect(() => {
    if (isInitialMount.current) return;

    const container = scrollRef.current;
    if (!container) return;

    const messageCount = items.filter((i) => i.kind === 'message').length;

    if (messageCount > prevCount) {
      // Só auto-scroll se o usuário está perto do bottom (últimos 200px)
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 200) {
        bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    }
  }, [items.length, prevCount]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto overscroll-contain"
      // Scroll behavior padrão — smooth apenas no auto-scroll manual
    >
      {/* Sentinela de load-more (topo) */}
      <LoadMoreTrigger onIntersect={onLoadMore} hasMore={hasMore} isFetching={isFetchingMore} />

      {/* Lista de itens */}
      <div className="flex flex-col py-2">
        {items.map((item, i) => {
          if (item.kind === 'day-separator') {
            return <DaySeparator key={`sep-${item.date}-${i}`} date={item.date} />;
          }
          return (
            <MessageBubble
              key={item.message.id}
              message={item.message}
              contactInitial={contactInitial}
            />
          );
        })}
      </div>

      {/* Âncora do bottom para scroll */}
      <div ref={bottomRef} aria-hidden="true" className="h-px" />
    </div>
  );
}

// ─── ConversationPanel ────────────────────────────────────────────────────────

interface ConversationPanelProps {
  conversationId: string;
  /** Callback para CTA de "usar template" no WindowNotice */
  onUseTemplate?: () => void;
  /** Abre o painel de contato (mobile: toque no nome do cliente no header). */
  onOpenContact?: (() => void) | undefined;
  /**
   * F43-S12: motivo fixo de somente leitura, decidido FORA do backend da
   * conversa — hoje só a caixa pessoal do vendedor usa isto
   * (`ConversationsLayout` com `fixedChannelId`), repassando o estado do
   * PRÓPRIO pareamento (`useMeuPareamento`, já carregado na tela) quando a
   * sessão não está `WORKING`. `undefined` = sem override — o composer segue
   * só `access.readOnlyReason` do backend (F43-S27).
   */
  composerDisabledReason?: string | undefined;
}

/**
 * ConversationPanel — painel completo da conversa.
 *
 * Integra lista de mensagens com paginação regressiva + realtime + compositor.
 */
export function ConversationPanel({
  conversationId,
  onUseTemplate,
  onOpenContact,
  composerDisabledReason,
}: ConversationPanelProps): React.JSX.Element {
  // ── Dados ──────────────────────────────────────────────────────────────────
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useMessages(conversationId);

  // Initial do contato para o avatar inbound (vem do detalhe da conversa).
  // O useConversation já é chamado pelo ConversationHeader — o resultado fica
  // no cache do TanStack Query, então não há fetch adicional aqui.
  const { data: convDetail } = useConversation(conversationId);
  const contactInitial = React.useMemo(() => {
    const name = convDetail?.data.contactName ?? convDetail?.data.contactRemoteId ?? null;
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
  }, [convDetail]);

  // Lead + canal para o banner de restrição acima do composer (F38-S04) —
  // mesmo `convDetail` já em cache, nenhum fetch adicional.
  const restrictionLeadId = convDetail?.data.leadId ?? null;
  const restrictionProvider = convDetail?.composerState.provider;

  // ── Briefing de handoff (F37-S06, doc 28 §2.2/§3 D3/D5) ─────────────────────
  // Flag off → nada renderiza nem chama API (DoD do slot): `briefingRef`
  // permanece lido do cache (já vem de `useConversation`, sem request extra),
  // mas o efeito abaixo só age com a flag ligada — e `HandoffBriefingModal`
  // só é montado com a flag ligada (ver JSX no fim do componente).
  const { enabled: briefingFlagEnabled } = useFeatureFlag(BRIEFING_FLAG_KEY);
  const briefingRef = convDetail?.data.briefing ?? null;

  // Sets persistidos por MONTAGEM do painel (nunca resetados enquanto a
  // conversa estiver aberta) — guardam contra StrictMode (efeito roda 2x em
  // dev), refetch (objeto novo a cada resposta) e o socket `briefing_ready`
  // (que invalida o detalhe de novo, mas não deve reabrir o que já foi visto).
  const openedBriefingIdsRef = React.useRef<Set<string>>(new Set());
  const ackedBriefingIdsRef = React.useRef<Set<string>>(new Set());
  const [openBriefingId, setOpenBriefingId] = React.useState<string | null>(null);
  // Conversa "dona" do modal aberto — o painel não é remontado por `key`
  // quando `selectedId` muda no `ConversationsLayout` (deep-link/troca de
  // conversa com o modal ainda aberto). Sem isto, `HandoffBriefingModal`
  // recebe o `conversationId` AO VIVO do painel e re-fetcharia (via
  // `useBriefing`, que é por conversa, não por briefing) o briefing da NOVA
  // conversa por baixo do modal que o usuário associava à conversa antiga —
  // achado MÉDIO do painel de revisão do PR #595. Guardamos a conversa da
  // abertura e fechamos (sem ack — o servidor decide se foi visto de fato)
  // assim que ela diverge da conversa atualmente selecionada.
  const openBriefingConversationIdRef = React.useRef<string | null>(null);
  const ackBriefing = useAckBriefing(conversationId);

  React.useEffect(() => {
    if (!briefingFlagEnabled) return;
    const toOpen = shouldOpenBriefingModal(briefingRef, openedBriefingIdsRef.current);
    if (toOpen === null) return;
    openedBriefingIdsRef.current.add(toOpen);
    openBriefingConversationIdRef.current = conversationId;
    setOpenBriefingId(toOpen);
  }, [briefingFlagEnabled, briefingRef, conversationId]);

  // Troca de conversa (deep-link) com o modal ainda aberto para a conversa
  // ANTERIOR → fecha sem ack. Não reabre nem re-associa: se a nova conversa
  // também tiver um briefing pendente, o efeito acima cuida dele no próximo
  // render com o guard normal de `shouldOpenBriefingModal`.
  // `useLayoutEffect` (não `useEffect`) para fechar ANTES do paint da troca de
  // conversa — evita 1 frame do `HandoffBriefingModal` renderizado com o
  // `conversationId` novo por baixo do estado "aberto" da conversa antiga.
  React.useLayoutEffect(() => {
    if (
      openBriefingId !== null &&
      openBriefingConversationIdRef.current !== null &&
      openBriefingConversationIdRef.current !== conversationId
    ) {
      setOpenBriefingId(null);
      openBriefingConversationIdRef.current = null;
    }
  }, [conversationId, openBriefingId]);

  const handleCloseBriefing = React.useCallback(() => {
    const current = openBriefingId;
    setOpenBriefingId(null);
    openBriefingConversationIdRef.current = null;
    if (!shouldFireBriefingAck(current, ackedBriefingIdsRef.current)) return;
    // Marca o Set só após a confirmação (ver `commitBriefingAck`) — se o POST
    // falhar, o Set não trava e uma tentativa futura ainda pode reenviar.
    void commitBriefingAck(current as string, ackedBriefingIdsRef.current, () =>
      ackBriefing.mutateAsync(),
    );
  }, [ackBriefing, openBriefingId]);

  // ── Realtime ───────────────────────────────────────────────────────────────
  // scope 'detail': dona do cache de detalhe/mensagens desta conversa + sala
  // socket (join/leave). ChatList (scope 'list') cuida do cache da lista —
  // ver cabeçalho de useConversationSocket.ts (bug do contador em dobro).
  useConversationSocket({ conversationId, scope: 'detail' });

  // ── Flatten mensagens (infinite query = páginas) ───────────────────────────
  // pages[0] = primeira fetch (mensagens mais recentes), pages[N] = mais antigas (load-more).
  // Backend retorna cada página em ASC (oldest→newest) — não invertemos o conteúdo.
  // Só invertemos a ordem das páginas para que páginas mais antigas fiquem no topo.
  const allMessages = React.useMemo<Message[]>(() => {
    if (!data) return [];
    return [...data.pages].reverse().flatMap((page) => page.data);
  }, [data]);

  const prevMessageCountRef = React.useRef(0);
  const prevCount = prevMessageCountRef.current;
  React.useEffect(() => {
    prevMessageCountRef.current = allMessages.length;
  });

  // ── Inserir separadores de dia ─────────────────────────────────────────────
  const listItems = React.useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];
    for (let i = 0; i < allMessages.length; i++) {
      const msg = allMessages[i];
      if (!msg) continue;
      const prev = allMessages[i - 1];
      const dayChanged = prev ? isDifferentDay(prev.createdAt, msg.createdAt) : true;
      if (dayChanged) {
        items.push({ kind: 'day-separator', date: formatDaySeparator(msg.createdAt) });
      }
      items.push({ kind: 'message', message: msg });
    }
    return items;
  }, [allMessages]);

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Modal do briefing — independente do estado de carregamento das mensagens
  // (o handoff pode acontecer numa conversa vazia). Flag off → `null`, nunca
  // montado (DoD: "flag off → nada renderiza nem chama API").
  const briefingModal =
    briefingFlagEnabled && openBriefingId !== null ? (
      <HandoffBriefingModal conversationId={conversationId} onClose={handleCloseBriefing} />
    ) : null;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <div className="flex flex-col h-full bg-surface-1">
          <ConversationHeader conversationId={conversationId} onOpenContact={onOpenContact} />
          <div className="flex-1 overflow-hidden">
            <MessageSkeleton />
          </div>
          <div className="border-t border-border bg-surface-1 h-14 animate-pulse" />
        </div>
        {briefingModal}
      </>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <>
        <div className="flex flex-col h-full bg-surface-1">
          <ConversationHeader conversationId={conversationId} onOpenContact={onOpenContact} />
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center"
              style={{ background: 'var(--danger-bg)', boxShadow: 'var(--elev-1)' }}
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="w-5 h-5 text-danger"
              >
                <circle cx="10" cy="10" r="8" />
                <path d="M10 7v4M10 13v.5" strokeLinecap="round" strokeWidth={2} />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-sans font-semibold text-ink text-sm">Erro ao carregar mensagens</p>
              <p className="font-sans text-xs text-ink-3 mt-1">
                Verifique sua conexão e tente novamente.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              className={cn(
                'font-sans text-sm font-semibold px-4 py-2 rounded-sm',
                '[background:var(--grad-azul)] text-white',
                '[box-shadow:var(--elev-2)]',
                'hover:-translate-y-0.5 hover:[box-shadow:var(--glow-azul)]',
                'active:translate-y-0',
                'transition-[transform,box-shadow] duration-fast ease',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
              )}
            >
              Tentar novamente
            </button>
          </div>
          <ContactRestrictionComposerBanner
            leadId={restrictionLeadId}
            provider={restrictionProvider}
          />
          <MessageComposer
            key={conversationId}
            conversationId={conversationId}
            {...(onUseTemplate !== undefined ? { onUseTemplate } : {})}
            {...(composerDisabledReason !== undefined ? { composerDisabledReason } : {})}
          />
        </div>
        {briefingModal}
      </>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (allMessages.length === 0) {
    return (
      <>
        <div className="flex flex-col h-full bg-surface-1">
          <ConversationHeader conversationId={conversationId} onOpenContact={onOpenContact} />
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--info-bg)', boxShadow: 'var(--elev-2)' }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="w-6 h-6 text-azul"
              >
                <path
                  d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-display font-semibold text-ink">Nenhuma mensagem ainda</p>
              <p className="font-sans text-xs text-ink-3 mt-1">
                Use o compositor abaixo para iniciar a conversa.
              </p>
            </div>
          </div>
          <ContactRestrictionComposerBanner
            leadId={restrictionLeadId}
            provider={restrictionProvider}
          />
          <MessageComposer
            key={conversationId}
            conversationId={conversationId}
            {...(onUseTemplate !== undefined ? { onUseTemplate } : {})}
            {...(composerDisabledReason !== undefined ? { composerDisabledReason } : {})}
          />
        </div>
        {briefingModal}
      </>
    );
  }

  // ── Estado principal ──────────────────────────────────────────────────────
  return (
    <>
      <div className="flex flex-col h-full bg-surface-1">
        <ConversationHeader conversationId={conversationId} />
        <MessageList
          items={listItems}
          onLoadMore={handleLoadMore}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          prevCount={prevCount}
          contactInitial={contactInitial}
        />
        <ContactRestrictionComposerBanner
          leadId={restrictionLeadId}
          provider={restrictionProvider}
        />
        {/* key: troca de conversa REMONTA o compositor (F51-S41) — gravação,
            preview de anexo, upload e rascunho não passam para a próxima. */}
        <MessageComposer
          key={conversationId}
          conversationId={conversationId}
          {...(onUseTemplate !== undefined ? { onUseTemplate } : {})}
          {...(composerDisabledReason !== undefined ? { composerDisabledReason } : {})}
        />
      </div>
      {briefingModal}
    </>
  );
}
